import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ApiService } from "api/api.service";
import type {
  T_AssistantContextData,
  T_AssistantOffer,
  T_AssistantTurn,
  T_BackendEffectiveContext,
  T_OfferAssistantNegotiationContext,
  T_OfferAssistantSession,
} from "dtos/offer-assistant.dto";
import { getErrorMessage } from "utils/functions";
import {
  envelopeToTurn,
  guessSkeleton,
  historyToSeeds,
  isContextAction,
  offersInTurn,
  refNo,
  variantNo,
  type T_SeedItem,
  type T_SkeletonKind,
} from "utils/offer-assistant.utils";

// ─── Types ────────────────────────────────────────────────────────────────────

export type T_ChatItem =
  | { id: string; role: "user"; text: string }
  | { id: string; role: "assistant"; turn: T_AssistantTurn }
  | { id: string; role: "pending"; guess: T_SkeletonKind }
  /**
   * `text` is the message that failed — empty when the bootstrap call failed.
   * `restored` = an error turn from history: shown without Retry and kept across new sends.
   */
  | { id: string; role: "error"; text: string; message: string; restored?: boolean };

export type T_ErrorChatItem = Extract<T_ChatItem, { role: "error" }>;

/** missing = no appkey/loan_id/source_uri · loading = restoring history · ready = conversation live */
export type T_StartStatus = "missing" | "loading" | "error" | "ready";

// ─── Context (read by blocks / cards, provided by <OfferAssistant>) ───────────

export type T_OfferAssistantApi = {
  /** Send a message as the user */
  send: (text: string) => void;
  /** Put a phrase in the composer without sending */
  insert: (text: string) => void;
  isSelected: (ref: string) => boolean;
  toggleSelect: (ref: string) => void;
  owed: number;
  busy: boolean;
};

export const OfferAssistantContext = createContext<T_OfferAssistantApi | null>(null);

export function useOfferAssistantApi(): T_OfferAssistantApi {
  const ctx = useContext(OfferAssistantContext);
  if (!ctx) throw new Error("useOfferAssistantApi must be used inside <OfferAssistantContext.Provider>");
  return ctx;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

let uid = 0;
const nextId = () => `oa${++uid}`;

const seedToItem = (s: T_SeedItem): T_ChatItem => {
  switch (s.role) {
    case "user":
      return { id: nextId(), role: "user", text: s.text };
    case "assistant":
      return { id: nextId(), role: "assistant", turn: s.turn };
    case "error":
      return { id: nextId(), role: "error", text: "", message: s.message, restored: true };
  }
};

export function useOfferAssistant({ application_id, loan_id, thread_id }: T_OfferAssistantNegotiationContext) {
  const hasIdentity = !!application_id && !!loan_id;

  const [items, setItems] = useState<T_ChatItem[]>([]);
  const [draft, setDraft] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [session, setSession] = useState<T_OfferAssistantSession | null>(null);
  const [isSeeded, setIsSeeded] = useState(false);

  // Guard against double-seeding (StrictMode runs effects twice).
  const seededRef = useRef(false);
  // Last EffectiveContext seen — lets update_context turns show which limits changed.
  const prevContext = useRef<T_BackendEffectiveContext | null>(null);

  const {
    data: historyData,
    isSuccess: isHistorySuccess,
    isFetching: isHistoryFetching,
    isError: isHistoryError,
    refetch: refetchHistory,
  } = ApiService.negotiation.useOfferAssistantHistory({ application_id, loan_id, thread_id, enabled: hasIdentity });
  const { mutateAsync: postChat } = ApiService.negotiation.useSendOfferAssistantMessage();

  const busy = items.some((i) => i.role === "pending");

  /** text = null bootstraps a new chat (no message). */
  const run = useCallback(
    async (text: string | null) => {
      if (!application_id || !loan_id) return;
      const userId = nextId();
      const pendId = nextId();
      setItems((prev) => [
        ...prev.filter((i) => i.role !== "error" || i.restored),
        ...(text ? [{ id: userId, role: "user", text } as T_ChatItem] : []),
        { id: pendId, role: "pending", guess: text ? guessSkeleton(text) : "cards" },
      ]);

      try {
        const res = await postChat({
          body: {
            application_id,
            loan_id,

            ...(thread_id ? { thread_id } : {}),
            ...(text ? { message: text } : {}),
          },
        });
        const envelope = res.result;
        setSession(envelope.session);
        // Adapt against the *previous* context first, then remember the new one.
        const turn = envelopeToTurn(envelope, prevContext.current);
        if (isContextAction(envelope.action) && envelope.data[0]) {
          prevContext.current = envelope.data[0] as T_BackendEffectiveContext;
        }
        setItems((prev) => prev.map((i) => (i.id === pendId ? { id: pendId, role: "assistant", turn } : i)));
      } catch (error) {
        // The message wasn't sent: drop the bubble, show the inline error, keep the text in the composer.
        setItems((prev) => [
          ...prev.filter((i) => i.id !== pendId && i.id !== userId),
          { id: nextId(), role: "error", text: text ?? "", message: getErrorMessage(error) },
        ]);
        if (text) setDraft((d) => (d.trim() ? d : text));
      }
    },
    [application_id, loan_id, thread_id, postChat],
  );

  // Seed once: restore history if the chat exists, otherwise bootstrap a new one.
  useEffect(() => {
    if (seededRef.current || !isHistorySuccess || isHistoryFetching) return;
    seededRef.current = true;
    setIsSeeded(true);

    const restored = historyData?.result;
    if (restored?.turns.length) {
      const { seeds, lastContext } = historyToSeeds(restored.turns, restored.session);
      setSession(restored.session);
      prevContext.current = lastContext;
      setItems(seeds.map(seedToItem));
    } else {
      void run(null);
    }
  }, [isHistorySuccess, isHistoryFetching, historyData, run]);

  const startStatus: T_StartStatus = !hasIdentity
    ? "missing"
    : isSeeded
      ? "ready"
      : isHistoryError
        ? "error"
        : "loading";

  const retryStart = useCallback(() => void refetchHistory(), [refetchHistory]);

  const send = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text || busy || startStatus !== "ready") return;
      setDraft("");
      void run(text);
      if (/^compare/i.test(text)) setSelected([]);
    },
    [busy, run, startStatus],
  );

  const retry = useCallback(
    (item: T_ErrorChatItem) => {
      if (!item.text) {
        void run(null);
        return;
      }
      if (draft.trim() === item.text) setDraft("");
      void run(item.text);
    },
    [draft, run],
  );

  const toggleSelect = useCallback(
    (ref: string) => setSelected((s) => (s.includes(ref) ? s.filter((r) => r !== ref) : [...s, ref].slice(-4))),
    [],
  );

  /** Every offer seen in this chat, keyed by ref — powers @-mentions. */
  const offers = useMemo<T_AssistantOffer[]>(() => {
    const map = new Map<string, T_AssistantOffer>();
    for (const it of items) {
      if (it.role !== "assistant") continue;
      for (const o of offersInTurn(it.turn)) if (o.kind !== "whatif") map.set(o.ref, o);
    }
    return [...map.values()].sort((a, b) => refNo(a.ref) - refNo(b.ref) || variantNo(a.ref) - variantNo(b.ref));
  }, [items]);

  /** Newest limits block in the chat — fallback for the "Custom limits" pill before a session arrives. */
  const latestContext = useMemo<T_AssistantContextData | null>(() => {
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i];
      if (it.role === "assistant" && (it.turn.action === "get_context" || it.turn.action === "update_context")) {
        return it.turn.data;
      }
    }
    return null;
  }, [items]);

  return {
    items,
    draft,
    setDraft,
    selected,
    toggleSelect,
    send,
    retry,
    retryStart,
    startStatus,
    busy,
    offers,
    session,
    latestContext,
  };
}

export type T_OfferAssistantState = ReturnType<typeof useOfferAssistant>;
